import React, { useCallback } from 'react';

import { Box } from '@rbilabs/components-library/build/components/layout';
import { VisuallyHidden } from '@rbilabs/components-library/build/components/visually-hidden';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { IBaseProps } from '@rbi-ctg/frontend';
import { IItem } from '@rbi-ctg/menu';
import ActionButton from 'components/action-button';
import { fullPageScreenHeaderHeight } from 'components/full-page-screen/styled/base';
import { brandFont } from 'components/layout/brand-font';
import defaultConfig from 'components/menu-order-wizard/config';
import Accordion from 'components/selection-container/accordion';
import { DialogFooter } from 'components/sticky-footer';
import { AddOnSelections, IAddOnSection, IAddOnSectionOption } from 'hooks/use-cart-add-ons';
import { useFormatCalories } from 'hooks/use-format-calories';
import { useAddOnsItemContainer } from 'pages/cart/addons-item-container/hook/use-addons-item-container';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMenuOptionsContext } from 'state/menu-options';
import { useOrderContext } from 'state/order';
import { useUIContext } from 'state/ui';
import { primitive } from 'styles/constants/primitives';
import { MAX_CART_QUANTITY } from 'utils/cart';
import { getItemCalories } from 'utils/menu';
import { escapeQuantity } from 'utils/wizard';

import AddExtrasContentItem from './add-extras-content-item';

interface IAddExtrasProps extends IBaseProps {
  addOnSelections: AddOnSelections;
  addOns: IAddOnSection[];
  addToCart: () => void;
  isEdit: boolean;
  updateQuantityForitem: (
    section: IAddOnSection,
    sectionOption: IAddOnSectionOption,
    option: IItem,
    quantity: number,
    keyId: string,
    isFreeAddon: boolean,
    maxQtdFree: number
  ) => void;
  totalPrice: number;
  totalQuantity: number;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  text-align: left;
  background-color: ${primitive.$white};

  & > fieldset {
    border: none;
    flex: 1;
    justify-content: unset;
  }

  ${Styles.desktop`
    & > fieldset:first-child {
      margin-top: ${fullPageScreenHeaderHeight};
    }
  `}
`;

const SubHeader = styled.span`
  font: ${brandFont.copyTwo};
  text-transform: none;
`;

const ButtonContainer = styled.div`
  width: 100%;
  padding: 16px;
`;

const AddExtras: React.FC<IAddExtrasProps> = ({
  addOns,
  addOnSelections,
  addToCart,
  isEdit,
  updateQuantityForitem,
  totalPrice,
  totalQuantity,
}) => {
  const { formatCurrencyForLocale } = useUIContext();
  const { formatMessage } = useIntl();
  const { pricingFunction } = useMenuOptionsContext();
  const { isServiceValid, countMaximumFreeOffer, addOnHasOffer, isItemsAcceptingFreeOffer } =
    useAddOnsItemContainer();
  const formatCalories = useFormatCalories();
  const { cartEntries } = useOrderContext();

  const getCalorieString = useCallback(
    (item: IItem) => formatCalories(getItemCalories(item)),
    [formatCalories]
  );
  const enableFreeAddonOnCartFeatureFlag = useFlag(LaunchDarklyFlag.ENABLE_FREE_ADDON_ON_CART);

  const getItemMax = useCallback(
    (
      itemOptionId: string,
      itemMax: number | null,
      sectionId: string,
      sectionMax: number | null
    ) => {
      const maxForThisItem = escapeQuantity(itemMax ?? MAX_CART_QUANTITY);
      if (!sectionMax || sectionMax === 0) {
        return maxForThisItem;
      }

      // How many are we at for the section currently?
      const totalForSection = Object.values(addOnSelections).reduce(
        (total, { sectionId: selectionSectionId, quantity: selectionQuantity }) => {
          return selectionSectionId === sectionId ? total + selectionQuantity : total;
        },
        0
      );

      // Did we hit the sectionMax?
      // If so return the current selection or zero
      if (totalForSection === sectionMax) {
        return addOnSelections[itemOptionId]?.quantity ?? 0;
      }

      // If not continue as normal
      return maxForThisItem;
    },
    [addOnSelections]
  );

  const listAddonsAvaliableforSection = (list: IAddOnSection[]): IAddOnSection[] => {
    const isExtrasAvaliable = ({
      showSectionItemsOnCart,
      sectionItemsServiceModes,
    }: IAddOnSection) => {
      const isSectionItemsAvaliableByService =
        showSectionItemsOnCart && isServiceValid(sectionItemsServiceModes);
      return !showSectionItemsOnCart || isSectionItemsAvaliableByService;
    };

    return list.filter(isExtrasAvaliable);
  };

  const renderExtraContentItem = useCallback(
    (
      key: string,
      addOnOption: IAddOnSectionOption,
      addOnSection: IAddOnSection,
      option: IItem,
      maxQtdFree = 0,
      isMaxFreeQtd = false,
      isFreeAddon = false
    ): JSX.Element => {
      return (
        <AddExtrasContentItem
          key={key}
          itemName={option.name.locale}
          itemCalories={getCalorieString(option)}
          index={addOnSelections[key]?.quantity ?? 0}
          onChange={newQuantity => {
            updateQuantityForitem(
              addOnSection,
              addOnOption,
              option,
              newQuantity,
              key,
              isFreeAddon,
              maxQtdFree
            );
          }}
          itemPrice={pricingFunction(option)}
          itemImage={option.image}
          isInCart={addOnSelections[key]?.data.name.locale === option.name.locale}
          max={getItemMax(
            key,
            isMaxFreeQtd ? maxQtdFree : addOnOption.maxAmount,
            addOnSection._id,
            addOnSection.maxAmount
          )}
          maxAmount={addOnSection.maxAmount}
        />
      );
    },
    [addOnSelections, getCalorieString, getItemMax, pricingFunction, updateQuantityForitem]
  );

  const renderAddExtrasContentItem = useCallback(
    (addOnOption: IAddOnSectionOption, addOnSection: IAddOnSection) => {
      const maxQtdFree = countMaximumFreeOffer(
        cartEntries,
        addOnOption.itemsAcceptingFreeOffers ? addOnOption.itemsAcceptingFreeOffers : undefined
      );

      const freeItemKey = addOnOption?.freeOption?._id;

      const isAddonAsFreeEnabled =
        addOnSection.enableAddonAsFreeItem && enableFreeAddonOnCartFeatureFlag;

      const isMaxFreeQtd = freeItemKey && addOnSelections[freeItemKey]?.quantity === maxQtdFree;

      const hasOffer = addOnHasOffer(cartEntries, addOnOption.itemsAcceptingFreeOffers);

      return isAddonAsFreeEnabled && !hasOffer ? null : (
        <>
          {isAddonAsFreeEnabled &&
            addOnOption.freeOption &&
            freeItemKey &&
            renderExtraContentItem(
              freeItemKey,
              addOnOption,
              addOnSection,
              addOnOption.freeOption,
              maxQtdFree,
              isMaxFreeQtd,
              true
            )}
          {(!isAddonAsFreeEnabled || isMaxFreeQtd) &&
            renderExtraContentItem(addOnOption._key, addOnOption, addOnSection, addOnOption.option)}
        </>
      );
    },
    [
      addOnHasOffer,
      addOnSelections,
      cartEntries,
      countMaximumFreeOffer,
      enableFreeAddonOnCartFeatureFlag,
      renderExtraContentItem,
    ]
  );

  return (
    <Container>
      {listAddonsAvaliableforSection(addOns).map(
        addOnSection =>
          isItemsAcceptingFreeOffer(addOnSection, cartEntries) && (
            <Accordion
              key={addOnSection._id}
              title={
                <Box flexDirection="column">
                  {addOnSection.name.locale}
                  {addOnSection.maxAmount > 0 && (
                    <SubHeader>
                      {formatMessage({ id: 'totalChoices' }, { amount: addOnSection.maxAmount })}
                    </SubHeader>
                  )}
                </Box>
              }
              openOnMount
            >
              <div
                data-testid={`add-on-section-${addOnSection.name.locale}`}
                role="group"
                aria-label={addOnSection.name.locale}
              >
                <VisuallyHidden>{addOnSection.name.locale}</VisuallyHidden>
                {addOnSection?.options?.map(addOnOption =>
                  renderAddExtrasContentItem(addOnOption, addOnSection)
                )}
              </div>
            </Accordion>
          )
      )}

      <DialogFooter
        elements={[
          {
            element: (
              <ButtonContainer>
                <ActionButton
                  variant={defaultConfig.buttonVariant}
                  aria-label={formatMessage(
                    { id: 'addQuantityAndItemWithPrice' },
                    {
                      itemName: `Extra${totalQuantity > 1 ? 's' : ''}`,
                      quantity: totalQuantity,
                      price: formatCurrencyForLocale(totalPrice),
                    }
                  )}
                  data-testid="extras-add-to-order-button"
                  perceptible
                  onClick={addToCart}
                  fullWidth
                >
                  {[
                    formatMessage({
                      id: isEdit ? 'updateCart' : 'addToOrder',
                    }),
                    formatCurrencyForLocale(totalPrice),
                  ].join(' — ')}
                </ActionButton>
              </ButtonContainer>
            ),
          },
        ]}
      />
    </Container>
  );
};

export default AddExtras;
